import { Box, Typography } from '@mui/material';
import { InputContainer } from '../../layout';
import { UseFieldType } from '../../hooks/useField';
import { useTranslationContext } from '../../context/TranslationContext';

type ColorPickerProps = {
  colorField: UseFieldType;
  disabled: boolean;
  onColorChange?: (color: string) => void;
};

const ColorPicker = ({ colorField, disabled, onColorChange }: ColorPickerProps) => {
  const t = useTranslationContext();
  const rawColorValue = colorField.attributes.value || '#1976d2';

  // Función para convertir cualquier formato de color a hexadecimal
  const convertToHex = (color: string): string => {
    // Si ya es hexadecimal, retornarlo
    if (color.startsWith('#')) {
      return color;
    }

    // Si es formato rgb o rgba
    const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (rgbMatch && rgbMatch[1] && rgbMatch[2] && rgbMatch[3]) {
      const r = parseInt(rgbMatch[1]);
      const g = parseInt(rgbMatch[2]);
      const b = parseInt(rgbMatch[3]);

      const toHex = (n: number) => {
        const hex = n.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      };

      return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    }

    // Si no se puede convertir, retornar color por defecto
    return '#1976d2';
  };

  const colorValue = convertToHex(rawColorValue);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    if (onColorChange) {
      onColorChange(newColor);
    } else {
      colorField.actions.changeValue(newColor);
    }
  };

  // Modo vista: mostrar solo el color como un cuadro
  if (disabled) {
    return (
      <InputContainer title={t('global.color', 'Color')}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              backgroundColor: colorValue,
              border: '1px solid rgba(0, 0, 0, 0.23)',
              borderRadius: 1,
            }}
          />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {colorValue}
          </Typography>
        </Box>
      </InputContainer>
    );
  }

  return (
    <InputContainer title={t('global.color', 'Color')}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <input
          type="color"
          value={colorValue}
          onChange={handleChange}
          disabled={disabled}
          style={{
            width: 60,
            height: 40,
            border: '1px solid rgba(0, 0, 0, 0.23)',
            borderRadius: 4,
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        />
      </Box>
    </InputContainer>
  );
};

export default ColorPicker;
